import React from "react";
import { Link } from "react-router";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { logger } from "@/lib/logger";
import { SELF_HEAL_RELOAD_KEY, isChunkLoadError, reloadOnce } from "@/lib/selfHeal";

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  /**
   * Clears a caught error when it changes. Layouts pass the current pathname
   * so navigating away (sidebar, back button) recovers without a reload.
   */
  resetKey?: string;
  /** Where the "back" link in the fallback goes. */
  homeHref?: string;
  homeLabel?: string;
}

interface RouteErrorBoundaryState {
  error: Error | null;
  errorResetKey: string | undefined;
}

/**
 * Catches render errors below a layout's <Outlet /> so a crashing page shows
 * a recoverable message instead of unmounting the whole app into a white
 * screen (#920). The surrounding layout (sidebar/navbar) stays usable.
 *
 * The app uses declarative <BrowserRouter> routes, where react-router's
 * `errorElement` does not apply, hence a class boundary.
 *
 * A failed chunk import (a lazy route whose bundle 404s during a rolling
 * deploy) also reloads the page, once per guard window (#917). If that reload
 * was already spent, the fallback stays, offering "Reload page" instead of
 * "Try again".
 */
export class RouteErrorBoundary extends React.Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { error: null, errorResetKey: undefined };

  static getDerivedStateFromError(error: Error): Partial<RouteErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: RouteErrorBoundaryProps,
    state: RouteErrorBoundaryState
  ): Partial<RouteErrorBoundaryState> | null {
    if (state.error === null) {
      // Remember which key the next error (if any) belongs to.
      return state.errorResetKey === props.resetKey ? null : { errorResetKey: props.resetKey };
    }
    if (state.errorResetKey !== props.resetKey) {
      return { error: null, errorResetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (isChunkLoadError(error) && reloadOnce(SELF_HEAL_RELOAD_KEY)) {
      logger.warn("route_chunk_load_reload", { message: error.message, path: this.props.resetKey });
      return;
    }
    logger.error("route_render_error", {
      message: error.message,
      name: error.name,
      path: this.props.resetKey,
      componentStack: info.componentStack ?? undefined,
    });
  }

  private readonly handleRetry = () => {
    this.setState({ error: null });
  };

  // React.lazy keeps a failed chunk import, so re-rendering would only throw it
  // again: a lazy page whose code did not load needs a fresh page load (#995).
  // The user asks for it, so the automatic reload's one-shot guard stays out.
  private readonly handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.error === null) {
      return this.props.children;
    }
    const { homeHref = "/", homeLabel = "Go to start page" } = this.props;
    const chunkFailed = isChunkLoadError(this.state.error);
    return (
      <Card role="alert" className="mx-auto mt-8 max-w-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden="true" />
            Something went wrong
          </CardTitle>
          <CardDescription>
            {chunkFailed
              ? "This page could not be loaded. Reload the page, or go back and continue from there."
              : "This page ran into an unexpected error. You can try again, or go back and continue from there."}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {chunkFailed ? (
            <Button type="button" onClick={this.handleReload}>
              Reload page
            </Button>
          ) : (
            <Button type="button" onClick={this.handleRetry}>
              Try again
            </Button>
          )}
          <Button asChild variant="outline">
            <Link to={homeHref}>{homeLabel}</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }
}
